import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import LeadSearch from "./pages/LeadSearch";
import MyLeads from "./pages/MyLeads";
import LeadProfile from "./pages/LeadProfile";
import AITools from "./pages/AITools";
import MarketingStudio from "./pages/MarketingStudio";
import Challenge from "./pages/Challenge";
import Outreach from "./pages/Outreach";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import NichePerformance from "./pages/NichePerformance";
import VerifyEmail from "./pages/VerifyEmail";
import Pricing from "./pages/Pricing";

// Legal Pages
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import DataRetention from "./pages/DataRetention";
import AboutUs from "./pages/AboutUs";

// Auth Pages
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

// Protected Route Component - Requires authentication
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Subscription Protected Route - Requires active subscription
const SubscriptionRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if email is verified
  if (!user?.email_verified) {
    return <Navigate to="/verify-pending" replace />;
  }

  // Check if has active subscription
  const status = user?.subscription_status;
  if (status !== 'active' && status !== 'trialing') {
    return <Navigate to="/pricing" replace />;
  }

  return children;
};

// Public Route - Redirect to dashboard if authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Verify Pending Page Component
const VerifyPending = () => {
  const { user, logout } = useAuth();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">📧</span>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Verify Your Email</h2>
        <p className="text-slate-600 mb-6">
          We've sent a verification link to <strong>{user?.email}</strong>. 
          Please check your inbox and click the link to continue.
        </p>
        <div className="space-y-3">
          <button 
            onClick={() => window.location.reload()} 
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700"
          >
            I've Verified My Email
          </button>
          <button 
            onClick={logout} 
            className="w-full text-slate-600 py-2 px-4 hover:text-slate-900"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/verify-pending" element={<ProtectedRoute><VerifyPending /></ProtectedRoute>} />

      {/* Legal Pages (Public) */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/data-retention" element={<DataRetention />} />
      <Route path="/about" element={<AboutUs />} />

      {/* Protected Routes (require subscription) */}
      <Route path="/dashboard" element={<SubscriptionRoute><Dashboard /></SubscriptionRoute>} />
      <Route path="/search" element={<SubscriptionRoute><LeadSearch /></SubscriptionRoute>} />
      <Route path="/leads" element={<SubscriptionRoute><MyLeads /></SubscriptionRoute>} />
      <Route path="/leads/:leadId" element={<SubscriptionRoute><LeadProfile /></SubscriptionRoute>} />
      <Route path="/ai-tools" element={<SubscriptionRoute><AITools /></SubscriptionRoute>} />
      <Route path="/marketing" element={<SubscriptionRoute><MarketingStudio /></SubscriptionRoute>} />
      <Route path="/outreach" element={<SubscriptionRoute><Outreach /></SubscriptionRoute>} />
      <Route path="/challenge" element={<SubscriptionRoute><Challenge /></SubscriptionRoute>} />
      <Route path="/performance" element={<SubscriptionRoute><NichePerformance /></SubscriptionRoute>} />
      <Route path="/analytics" element={<SubscriptionRoute><Analytics /></SubscriptionRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
